/**
 * Shared Highcharts Stock scaffolding for the dashboard time-series charts
 * (the Location Details tabs and the multi-location Strength / Temperature /
 * Weather views), styled after the Highcharts line-time-series demo:
 * drag-to-zoom with shift-drag panning, x-axis crosshair, short axis date
 * labels with the full date carried by the tooltip instead, no scrollbar
 * (the navigator covers panning) and compact sizing on small screens.
 */
import Highcharts from 'highcharts/highstock';
import { timeZoneLabel } from '../../utils/datetime';

/** `%Z` in a chart date format: the short name of the chart's timezone at
 *  that instant ("AEST" / "AEDT", daylight saving aware), so a tooltip says
 *  which clock its time is on. Highcharts calls custom formats with its
 *  Time object (which carries the chart's `time.timezone`) as `this`. Test
 *  stubs of Highcharts have no `dateFormats`, so registration is skipped. */
const registerZoneFormat = (): void => {
  const formats = (Highcharts as { dateFormats?: Record<string, unknown> }).dateFormats;
  if (!formats) return;
  formats.Z = function zoneName(this: { timezone?: string } | undefined, timestamp: number): string {
    return timeZoneLabel(timestamp, this?.timezone);
  };
};
registerZoneFormat();

/** Soft gradient fill under a series line, matching the Highcharts
 *  time-series demo styling. */
export const areaFill = (color: string): Highcharts.GradientColorObject => ({
  linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
  stops: [
    [0, Highcharts.color(color).setOpacity(0.3).get('rgba') as string],
    [1, Highcharts.color(color).setOpacity(0.02).get('rgba') as string],
  ],
});

// Legacy ConXedge range buttons used by the Location Details tabs.
const DEFAULT_RANGE_BUTTONS: Highcharts.RangeSelectorButtonsOptions[] = [
  { type: 'hour', count: 1, text: '1h' },
  { type: 'hour', count: 2, text: '2h' },
  { type: 'hour', count: 3, text: '3h' },
  { type: 'hour', count: 6, text: '6h' },
  { type: 'day', count: 1, text: '1d' },
  { type: 'all', text: 'All' },
];

export interface BaseTimeSeriesConfig {
  /** IANA timezone of the pour so timestamps render in the project's local
   *  time instead of the browser's. */
  timezoneIana?: string;
  height?: number;
  /** Passed as a parameter (rather than overridden via Highcharts.merge)
   *  because merge combines arrays index-wise — overriding a longer button
   *  array with a shorter one would leave stale trailing buttons. */
  rangeButtons?: Highcharts.RangeSelectorButtonsOptions[];
  /** Index into rangeButtons preselected on load; defaults to all data. */
  selectedRangeButton?: number;
}

export const getBaseTimeSeriesOptions = (
  title: string,
  {
    timezoneIana,
    height = 500,
    rangeButtons = DEFAULT_RANGE_BUTTONS,
    selectedRangeButton,
  }: BaseTimeSeriesConfig = {},
): Highcharts.Options => ({
  chart: {
    height,
    zooming: { type: 'x' },
    panning: { enabled: true, type: 'x' },
    panKey: 'shift',
  },
  time: timezoneIana ? { timezone: timezoneIana } : undefined,
  title: { text: title },
  subtitle: {
    text: 'Click and drag in the chart to zoom in, hold Shift to pan',
    style: { color: '#999999', fontSize: '11px' },
  },
  xAxis: {
    type: 'datetime',
    ordinal: false,
    crosshair: true,
  },
  tooltip: {
    // Axis labels stay short (time / day only); the tooltip carries the
    // full date context instead, with the timezone it is shown in (%Z).
    xDateFormat: '%A, %e %b %Y, %H:%M %Z',
  },
  rangeSelector: {
    buttons: rangeButtons,
    selected: selectedRangeButton,
  },
  // Range buttons + drag-zoom + shift-drag panning cover navigation; the
  // navigator mini-chart and scrollbar underneath are visual noise.
  navigator: { enabled: false },
  scrollbar: { enabled: false },
  responsive: {
    rules: [
      {
        condition: { maxWidth: 600 },
        chartOptions: {
          chart: { height: 380 },
          subtitle: { text: '' },
          rangeSelector: { inputEnabled: false },
          // Drop the y-axis title on phones to reclaim horizontal space for the
          // plot. Empty string (not undefined) so Highcharts.merge applies it;
          // a falsy title renders nothing and reserves no space.
          yAxis: { title: { text: '' } },
        },
      },
    ],
  },
});
